import { Box } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

export interface PaloTotalsEntry {
    label: ReactNode;
    value: ReactNode;
    /** Emphasised entry — the amount the document comes to. */
    total?: boolean;
}

export interface PaloTotalsBarProps {
    /** Agreed terms and the fields that change the sum, on the left. */
    facts?: ReactNode;
    /** The numbers, on the right. */
    entries: PaloTotalsEntry[];
}

/**
 * One row carrying everything a document adds up to, above the action bar.
 *
 * The invoice used to show its totals twice: once under the position table and
 * once in a fixed "Gesamtübersicht" of some 300 pixels. On a 13-inch screen
 * that left 19 pixels for the form, and the positions could not be seen at all
 * (Roland Niedermayr, 2026-09-23). Both are replaced by this row of about 50.
 */
export const PaloTotalsBar = ({ facts, entries }: PaloTotalsBarProps): ReactElement => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            // Narrow windows scroll the row rather than stacking it into three.
            overflowX: 'auto',
        }}
    >
        {facts && (
            // The facts take whatever the numbers leave, so text that can be
            // cut is cut at the end of the row and not at a width of its own
            // (#2231 R-125: the delivery terms broke off mid-word with half
            // the row empty beside them).
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', minWidth: 0, flex: 1 }}>{facts}</Box>
        )}

        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'flex-end', gap: '22px', flexShrink: 0 }}>
            {entries.map((entry, index) => (
                <Box key={index} sx={{ textAlign: 'right' }}>
                    <Box sx={{ fontSize: '10px', color: c.ink3, whiteSpace: 'nowrap' }}>{entry.label}</Box>
                    <Box
                        sx={{
                            fontFamily: tokens.font.sans,
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 620,
                            whiteSpace: 'nowrap',
                            fontSize: entry.total ? '17px' : '13px',
                            color: entry.total ? c.ink : c.ink2,
                        }}
                    >
                        {entry.value}
                    </Box>
                </Box>
            ))}
        </Box>
    </Box>
);

/** A named fact in the totals bar: the agreed terms, an input, a switch. */
export const PaloTotalsFact = ({
    label,
    title,
    flexible,
    children,
}: {
    label: ReactNode;
    /** Full text for the tooltip, where the value is cut. */
    title?: string;
    /**
     * Gives way when the row runs out of width — for text that can be cut, such
     * as the agreed terms. Everything else keeps its width, so the amounts stay
     * where they are instead of being pushed out of sight.
     */
    flexible?: boolean;
    children: ReactNode;
}): ReactElement => (
    <Box sx={{ minWidth: flexible ? 60 : 0, flex: flexible ? 1 : '0 0 auto', overflow: 'hidden' }} title={title}>
        <Box
            sx={{
                fontSize: '10px',
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: c.ink3,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </Box>
        <Box
            sx={{
                fontSize: '12.5px',
                color: c.ink2,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                maxWidth: flexible ? 'none' : 260,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </Box>
    </Box>
);
